/**
 * How a customer is named to the merchant.
 *
 * Email is deliberately not part of it. For a walk-in it is a synthetic address
 * (`anon+<uid>@hobbyscan.invalid`) that means nothing to anyone, and for a
 * registered customer it is personal data with no reason to be on a list the
 * shop floor can read over someone's shoulder.
 *
 * A guest is marked as such so the merchant can tell who they can follow up with
 * and who only exists inside this shop's session — "Volodymyr - Guest". A guest
 * who never introduced themselves is already stored as "Guest", so the suffix is
 * dropped rather than repeated.
 */
export function customerLabel({
  name,
  isGuest,
  isPos = false,
}: {
  name: string;
  isGuest: boolean;
  isPos?: boolean;
}): string {
  const base = name?.trim() || (isGuest ? "Guest" : "Customer");
  if (isPos) return `POS - ${base}`;
  return isGuest && base !== "Guest" ? `${base} - Guest` : base;
}

/** The avatar letter: the name's first, as iOS shows it, so a counter customer is not "P" for POS. */
export function customerInitial(customer: { name: string; isGuest: boolean; isPos?: boolean }): string {
  const named = customer.name?.trim() || customerLabel(customer);
  return (named.charAt(0) || "?").toUpperCase();
}

/** Adding a customer threw on its way: they may have been made, so the list is read again. */
export const CUSTOMER_ADD_FAILED_ON_THE_WAY =
  "Something went wrong while adding the customer. Check the list before adding them again.";
